import useCustomDataSource from "components/ui/customs/useCustomDataSource";
import * as htmlToImage from "html-to-image";
import {
  cloneElement,
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

const SlideSaverContext = createContext();

const waitForPaint = () =>
  new Promise((resolve) =>
    requestAnimationFrame(() => requestAnimationFrame(resolve)),
  );

const languages = [
  { code: "en", label: "English", suffix: "" },
  { code: "fr", label: "French", suffix: " - FR" },
];

async function toPngFullTable(node, options) {
  // Save inline styles to restore later
  const prev = {
    overflow: node.style.overflow,
    height: node.style.height,
    maxHeight: node.style.maxHeight,
  };

  // Also handle scrollable TBODYs
  const tbodies = Array.from(node.querySelectorAll("tbody"));
  const tbodiesPrev = tbodies.map((t) => ({
    el: t,
    display: t.style.display,
    overflow: t.style.overflow,
    maxHeight: t.style.maxHeight,
    height: t.style.height,
  }));

  try {
    Object.assign(node.style, {
      overflow: "visible",
      height: "auto",
      maxHeight: "none",
    });
    tbodies.forEach((t) =>
      Object.assign(t.style, {
        display: "",
        overflow: "visible",
        height: "auto",
        maxHeight: "none",
      }),
    );

    // If any ancestor scrolls:
    let p = node.parentElement;
    const parents = [];
    while (p) {
      parents.push({ el: p, overflow: p.style.overflow });
      p = p.parentElement;
    }
    parents.forEach(({ el }) => (el.style.overflow = "visible"));

    const dataUrl = await htmlToImage.toPng(node, {
      // lower pixelRatio if output would exceed canvas limits
      // pixelRatio: Math.min(window.devicePixelRatio, 2),
      style: {
        // make sure nothing is clipped in the cloned tree
        overflow: "visible",
      },
      ...options,
    });

    return dataUrl;
  } finally {
    // Restore styles
    Object.assign(node.style, prev);
    tbodiesPrev.forEach(({ el, display, overflow, height, maxHeight }) =>
      Object.assign(el.style, { display, overflow, height, maxHeight }),
    );
  }
}

export const SlideSaverProvider = ({ children, name }) => {
  const [slides, setSlides] = useState([]);
  const { i18n } = useTranslation("translation");
  const [saved, setSaved] = useState(null);
  const [language, setLanguage] = useState(null);
  const [savingState, setSavingState] = useState("IDLE"); // IDLE, SAVING, ERROR, READY
  const { createOneAsync, updateOneAsync } = useCustomDataSource(
    "visualization.ScreenshotsRecord",
  );
  const { createOneAsync: createOneScreenshotAsync } = useCustomDataSource(
    "visualization.Screenshot",
  );

  const addSlide = (slide) => {
    setSlides((slides) => [...slides, slide]);
  };

  const removeSlide = (slide) => {
    setSlides((slides) =>
      slides.filter((el) => el.elementRef !== slide.elementRef),
    );
  };

  const saveOneSlide = async (slide, recordId) => {
    const node = slide.elementRef.current;
    if (!node) {
      throw new Error(`Slide "${slide.name}" is not mounted (ref is null)`);
    }
    const dataUrl = await toPngFullTable(node, {
      skipFonts: true,
    });
    return createOneScreenshotAsync({
      data: {
        record: recordId,
        filename: slide.name,
        screenshot: {
          content: dataUrl,
          content_type: "image/png",
          name: slide.name,
          size: dataUrl.length,
        },
      },
    }).then((data) => {
      setSaved((saved) => (saved || 0) + 1);
      return data;
    });
  };

  const saveSlides = async () => {
    setSavingState("SAVING");
    const previousLanguage = i18n.language;
    let hasError = false;
    for (const lang of languages) {
      setLanguage(lang);
      setSaved(0);
      await i18n.changeLanguage(lang.code);
      await waitForPaint();
      const recordName = name + lang.suffix;
      const record = await createOneAsync({ data: { name: recordName } });
      const allSaves = slides.map((slide) => {
        return saveOneSlide(slide, record.id);
      });
      try {
        await Promise.all(allSaves);
        await updateOneAsync({
          id: record.id,
          data: { name: recordName, status: "READY" },
        });
      } catch (error) {
        await updateOneAsync({
          id: record.id,
          data: { name: recordName, status: "ERROR" },
        });
        console.error(`Error saving slides (${lang.code}):`, error);
        hasError = true;
      }
    }
    setLanguage(null);
    setSavingState(hasError ? "ERROR" : "READY");
    i18n.changeLanguage(previousLanguage);
  };

  return (
    <SlideSaverContext.Provider
      value={{
        fullname: name,
        saveSlides,
        addSlide,
        removeSlide,
        saved,
        slides,
        savingState,
        language,
      }}
    >
      {children}
    </SlideSaverContext.Provider>
  );
};

export const SlideSavedCounter = () => {
  const { useGetLastByName } = useCustomDataSource(
    "visualization.ScreenshotsRecord",
  );
  const { saved, slides, fullname, savingState, language } =
    useContext(SlideSaverContext);
  const {
    data: lastRecord,
    isSuccess,
    isError,
  } = useGetLastByName({ name: fullname });

  if (savingState === "IDLE") {
    if (isSuccess && lastRecord) {
      return "Last saved: " + new Date(lastRecord.created_at).toLocaleString();
    }
    if (isSuccess && !lastRecord) {
      return "Last saved: Never";
    }
    if (isError) {
      return "Last saved: error";
    }
    return "Last saved: loading...";
  } else if (savingState === "SAVING") {
    return `Saving slides in ${language?.label}... (${saved || 0} / ${
      slides.length
    })`;
  } else if (savingState === "ERROR") {
    return `Error saving slides for ${fullname}`;
  } else if (savingState === "READY") {
    return `All slides saved for ${fullname} at ${new Date().toLocaleString()}`;
  }
};

export const SlideToSave = ({ children, name }) => {
  const { addSlide, removeSlide } = useContext(SlideSaverContext);
  const ref = useRef(null);

  useEffect(() => {
    // Enregistrement/desenregistrement INCONDITIONNELS : au demontage React
    // detache le ref (ref.current === null) avant ce cleanup, donc une garde
    // `if (ref.current)` empecherait removeSlide de s'executer et laisserait des
    // refs orphelins (current=null) s'accumuler dans `slides` -> "ref is null" au save.
    // removeSlide filtre par identite de ref (stable), il retire la bonne entree.
    addSlide({ name, elementRef: ref });
    return () => {
      removeSlide({ name, elementRef: ref });
    };
  }, [name]);

  return cloneElement(children, { ref });
};

export const useSlideSaver = () => {
  const { saveSlides, fullname } = useContext(SlideSaverContext);
  return { saveSlides, fullname };
};
